import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { setMongoUser, setTutorialActiveStepIndex } from '../store/authSlice';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { apiService } from '../services/apiService';
import {
  getStepsForRole,
  TutorialStep,
} from '../components/tutorial/tutorialSteps';
import { tutorialConfig } from '../config';

interface TutorialContextValue {
  isRunning: boolean;
  isTutorialCompleted: boolean;
  isLoading: boolean;
  isTutorialInProgress: boolean;
  startTutorial: () => void;
  stopTutorial: () => void;
  completeTutorial: () => Promise<void>;
  resetTutorial: () => Promise<void>;
}

const TutorialContext = createContext<TutorialContextValue | undefined>(undefined);

// Helper to get page path
const getPagePath = (page: string): string => {
  const pageToPath: Record<string, string> = {
    'dashboard': '/dashboard',
    'knowledge-base': '/knowledge-base',
    'training': '/training',
    'training-builder': '/training/builder',
    'calendar': '/calendar',
    'settings': '/settings',
  };
  return pageToPath[page] || '/dashboard';
};

// Helper to get current page from path
const getCurrentPage = (pathname: string): string => {
  if (pathname.startsWith('/training/builder')) return 'training-builder';
  if (pathname.startsWith('/training')) return 'training';
  if (pathname.startsWith('/knowledge-base')) return 'knowledge-base';
  if (pathname.startsWith('/calendar')) return 'calendar';
  if (pathname.startsWith('/settings')) return 'settings';
  return 'dashboard';
};

export const TutorialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { mongoUser } = useAppSelector((state) => state.auth);
  const { isAdmin, isSuperAdmin } = useRoleAccess();

  const [isRunning, setIsRunning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const tourRef = useRef<Shepherd.Tour | null>(null);
  const allStepsRef = useRef<TutorialStep[]>([]);
  const currentStepIndexRef = useRef(0);
  const isNavigatingRef = useRef(false);
  const pendingStepIndexRef = useRef<number | null>(null);
  const hasCreatedTourRef = useRef(false);

  const isTutorialCompleted = mongoUser?.preferences?.tutorialCompleted ?? false;

  // Complete tutorial and persist to backend
  const completeTutorial = useCallback(async () => {
    console.log('[Tutorial] completeTutorial called');
    setIsLoading(true);
    try {
      // Destroy tour without triggering cancel handlers
      if (tourRef.current) {
        tourRef.current.hide();
        tourRef.current = null;
      }

      await apiService.put('/auth/profile', {
        preferences: { tutorialCompleted: true }
      });

      if (mongoUser) {
        dispatch(setMongoUser({
          ...mongoUser,
          preferences: mongoUser.preferences
            ? { ...mongoUser.preferences, tutorialCompleted: true }
            : { theme: 'light' as const, notifications: { email: true, push: true }, tutorialCompleted: true }
        }));
      }

      setIsRunning(false);
      currentStepIndexRef.current = 0;
      isNavigatingRef.current = false;
      pendingStepIndexRef.current = null;
      // Clear Redux tutorial state
      dispatch(setTutorialActiveStepIndex(null));
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to save tutorial completion:', error);
    } finally {
      setIsLoading(false);
    }
  }, [mongoUser, dispatch, navigate]);

  // Stop tutorial without completing (just hide, don't save)
  const stopTutorial = useCallback(() => {
    console.log('[Tutorial] stopTutorial called');
    if (tourRef.current) {
      tourRef.current.hide();
      tourRef.current = null;
    }
    setIsRunning(false);
    currentStepIndexRef.current = 0;
    isNavigatingRef.current = false;
    pendingStepIndexRef.current = null;
    // Clear Redux tutorial state
    dispatch(setTutorialActiveStepIndex(null));
  }, [dispatch]);

  // Create and start tour from a given step index
  const createAndStartTour = useCallback((startIndex: number) => {
    // Prevent duplicate calls
    if (hasCreatedTourRef.current) {
      console.log('[Tutorial] Skipping duplicate createAndStartTour call');
      return;
    }

    const allSteps = allStepsRef.current;
    const totalSteps = allSteps.length;

    if (startIndex >= totalSteps) {
      completeTutorial();
      return;
    }

    console.log('[Tutorial] Creating tour starting at step:', startIndex);
    hasCreatedTourRef.current = true;

    // Clean up existing tour (just hide, don't cancel)
    if (tourRef.current) {
      tourRef.current.hide();
      tourRef.current = null;
    }

    // Create new tour
    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        classes: 'shepherd-theme-custom',
        scrollTo: { behavior: 'smooth', block: 'center' },
        cancelIcon: { enabled: true },
      },
    });

    // Store references for use in button actions
    const navRef = isNavigatingRef;
    const pendingRef = pendingStepIndexRef;
    const stepIndexRef = currentStepIndexRef;
    const stepsRef = allStepsRef;

    // Add all steps to the tour
    allSteps.forEach((step, index) => {
      const isFirstStep = index === 0;
      const isLastStep = index === totalSteps - 1;

      // Handle clickSelector before showing step
      const beforeShowPromise = step.clickSelector
        ? () => new Promise<void>((resolve) => {
            const el = document.querySelector(step.clickSelector!) as HTMLElement;
            if (el) el.click();
            setTimeout(resolve, 150);
          })
        : undefined;

      tour.addStep({
        id: `step-${index}`,
        title: step.popover?.title,
        text: `<p>${step.popover?.description || ''}</p><p class="shepherd-progress">${index + 1} of ${totalSteps}</p>`,
        attachTo: step.element ? { element: step.element, on: (step.popover?.side || 'bottom') as 'top' | 'bottom' | 'left' | 'right' } : undefined,
        beforeShowPromise,
        buttons: [
          // Back button
          {
            text: 'Back',
            classes: 'shepherd-button-secondary',
            disabled: isFirstStep,
            action: function() {
              if (index > 0) {
                const prevStep = stepsRef.current[index - 1];
                const prevPage = prevStep.page || 'dashboard';
                const currentPage = getCurrentPage(window.location.pathname);

                console.log('[Tutorial] Back clicked:', { index, prevPage, currentPage });

                if (prevPage !== currentPage) {
                  // Navigate to previous page
                  navRef.current = true;
                  pendingRef.current = index - 1;
                  stepIndexRef.current = index - 1;
                  hasCreatedTourRef.current = false; // Allow new tour creation
                  // Update Redux state before navigating
                  dispatch(setTutorialActiveStepIndex(index - 1));
                  tour.hide();
                  navigate(getPagePath(prevPage));
                } else {
                  stepIndexRef.current = index - 1;
                  tour.back();
                }
              }
            },
          },
          // Next button
          {
            text: isLastStep ? 'Finish Tour' : 'Next',
            classes: 'shepherd-button-primary',
            action: function() {
              if (isLastStep) {
                completeTutorial();
              } else {
                const nextStep = stepsRef.current[index + 1];
                const nextPage = nextStep.page || 'dashboard';
                const currentPage = getCurrentPage(window.location.pathname);

                console.log('[Tutorial] Next clicked:', { index, nextPage, currentPage });

                if (nextPage !== currentPage) {
                  // Navigate to next page
                  navRef.current = true;
                  pendingRef.current = index + 1;
                  stepIndexRef.current = index + 1;
                  hasCreatedTourRef.current = false; // Allow new tour creation
                  // Update Redux state before navigating
                  dispatch(setTutorialActiveStepIndex(index + 1));
                  tour.hide();
                  navigate(getPagePath(nextPage));
                } else {
                  stepIndexRef.current = index + 1;
                  tour.next();
                }
              }
            },
          },
        ],
        when: {
          show: () => {
            stepIndexRef.current = index;
            // Update Redux state when step is shown
            dispatch(setTutorialActiveStepIndex(index));
            console.log('[Tutorial] Showing step:', index, step.popover?.title);
          },
        },
      });
    });

    // Handle X button click - complete the tutorial
    tour.on('cancel', () => {
      console.log('[Tutorial] Tour cancelled (X clicked), isNavigating:', navRef.current);
      // Only complete if NOT navigating between pages
      if (!navRef.current) {
        completeTutorial();
      }
    });

    tourRef.current = tour;

    // Start the tour at the correct step
    tour.start();
    if (startIndex > 0) {
      tour.show(`step-${startIndex}`);
    }
  }, [completeTutorial, navigate, dispatch]);

  // Start the tutorial
  const startTutorial = useCallback(() => {
    // Check if tutorial is disabled via config or for super admin users
    if (tutorialConfig.disabled) {
      console.log('[Tutorial] Tutorial disabled via config.tutorial.disabled');
      return;
    }
    if (isSuperAdmin) {
      console.log('[Tutorial] Tutorial disabled for super admin users');
      return;
    }

    const steps = getStepsForRole(isAdmin);
    allStepsRef.current = steps;
    currentStepIndexRef.current = 0;
    isNavigatingRef.current = false;
    pendingStepIndexRef.current = null;
    hasCreatedTourRef.current = false;

    console.log('[Tutorial] Starting tutorial with', steps.length, 'steps, isAdmin:', isAdmin);
    setIsRunning(true);
    // Set Redux tutorial state to step 0
    dispatch(setTutorialActiveStepIndex(0));

    if (location.pathname !== '/dashboard') {
      isNavigatingRef.current = true;
      pendingStepIndexRef.current = 0;
      navigate('/dashboard');
    } else {
      setTimeout(() => createAndStartTour(0), 100);
    }
  }, [isAdmin, isSuperAdmin, location.pathname, navigate, createAndStartTour, dispatch]);

  // Reset tutorial for restart
  const resetTutorial = useCallback(async () => {
    setIsLoading(true);
    try {
      await apiService.put('/auth/profile', {
        preferences: { tutorialCompleted: false }
      });

      if (mongoUser) {
        dispatch(setMongoUser({
          ...mongoUser,
          preferences: mongoUser.preferences
            ? { ...mongoUser.preferences, tutorialCompleted: false }
            : { theme: 'light' as const, notifications: { email: true, push: true }, tutorialCompleted: false }
        }));
      }

      navigate('/dashboard');
      setTimeout(() => {
        startTutorial();
        setIsLoading(false);
      }, 500);
    } catch (error) {
      console.error('Failed to reset tutorial:', error);
      setIsLoading(false);
    }
  }, [navigate, startTutorial, mongoUser, dispatch]);

  // Handle navigation completion - resume tour after page change
  useEffect(() => {
    if (!isRunning) return;
    if (!isNavigatingRef.current) return;
    if (pendingStepIndexRef.current === null) return;

    const pendingIndex = pendingStepIndexRef.current;
    const allSteps = allStepsRef.current;
    if (pendingIndex >= allSteps.length) return;

    const expectedStep = allSteps[pendingIndex];
    const expectedPage = expectedStep.page || 'dashboard';
    const currentPage = getCurrentPage(location.pathname);

    console.log('[Tutorial] Navigation effect:', { currentPage, expectedPage, pendingIndex, isNavigating: isNavigatingRef.current });

    if (currentPage === expectedPage) {
      console.log('[Tutorial] Navigation complete, resuming at step:', pendingIndex);

      // Clear navigation state
      isNavigatingRef.current = false;
      pendingStepIndexRef.current = null;

      // Longer delay for Settings and Calendar pages
      const delay = (currentPage === 'settings' || currentPage === 'calendar') ? 1500 : 500;
      setTimeout(() => {
        createAndStartTour(pendingIndex);
      }, delay);
    }
  }, [location.pathname, isRunning, createAndStartTour]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (tourRef.current) {
        tourRef.current.hide();
      }
    };
  }, []);

  const value: TutorialContextValue = {
    isRunning,
    isTutorialCompleted,
    isLoading,
    isTutorialInProgress: isRunning || isNavigatingRef.current,
    startTutorial,
    stopTutorial,
    completeTutorial,
    resetTutorial,
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
      <style>{`
        /* Shepherd.js custom theme */
        .shepherd-theme-custom {
          max-width: 500px;
        }

        .shepherd-element {
          background: var(--surface);
          border-radius: var(--radius-card);
          box-shadow: var(--elev-menu);
        }

        .shepherd-text {
          font-size: var(--text-body-s);
          line-height: var(--lh-body);
          color: var(--ink-secondary);
          padding: 1rem;
        }

        .shepherd-text p {
          margin: 0 0 0.5rem 0;
        }

        .shepherd-text p:last-child {
          margin-bottom: 0;
        }

        .shepherd-text b {
          color: var(--ink);
          font-weight: var(--fw-semibold);
        }

        /* The step counter is metadata, not emphasis. It was signature-blue,
           which put a second accent in a popup that already has one on its
           primary button — the 90/10 rule allows exactly one. */
        .shepherd-progress {
          font-size: var(--text-caption);
          color: var(--ink-muted);
          font-weight: var(--fw-medium);
          margin-top: 1rem !important;
        }

        .shepherd-header {
          background: var(--surface);
          padding: 1rem 1rem 0 1rem;
          border-radius: var(--radius-card) var(--radius-card) 0 0;
        }

        .shepherd-title {
          font-size: var(--text-body-l);
          font-weight: var(--fw-semibold);
          color: var(--ink);
        }

        .shepherd-cancel-icon {
          color: var(--ink-disabled);
          font-size: 1.5rem;
        }

        .shepherd-cancel-icon:hover {
          color: var(--ink-muted);
        }

        .shepherd-footer {
          padding: 0.75rem 1rem 1rem 1rem;
          border-top: none;
        }

        .shepherd-button {
          border-radius: var(--radius-control);
          padding: 8px 16px;
          font-weight: var(--fw-semibold);
          font-size: var(--text-body-s);
          cursor: pointer;
          transition: background-color 0.2s;
        }

        /* SPECIFICITY MATTERS HERE, and getting it wrong is invisible until
           you hover. shepherd.css ships its own themed rules:

             .shepherd-button.shepherd-button-secondary            (0,2,0)
             .shepherd-button:not(:disabled):hover                 (0,3,0)
             .shepherd-button.shepherd-button-secondary:not(:disabled):hover  (0,4,0)

           A single-class selector loses to every one of those, so the first
           version of this theme left the secondary button entirely unstyled
           and both buttons snapping back to shepherd's blue and grey on
           hover. Each rule below therefore matches or exceeds the shepherd
           rule it is replacing. Do not "simplify" these selectors. */
        .shepherd-button.shepherd-button-secondary {
          background: var(--surface);
          color: var(--ink);
          border: 1px solid var(--border);
        }

        .shepherd-button.shepherd-button-secondary:not(:disabled):hover {
          background: var(--surface-subtle);
          color: var(--ink);
        }

        .shepherd-button.shepherd-button-secondary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* The one element in the tour allowed to fill with the signature
           colour — the primary action. Same rule as ui/Button.tsx. */
        .shepherd-button.shepherd-button-primary {
          background: var(--signature);
          color: var(--on-signature);
          border: none;
        }

        /* Hover lifts toward white rather than deepening. Derived from the
           token with color-mix so a re-theme carries it, instead of baking a
           second copper literal into the file.

           92% is not arbitrary: white text on the signature is 5.42:1, and
           lightening spends that fast — 8% lighter still clears WCAG AA at
           4.64:1, while 12% lighter is already 4.28:1 and fails. This is the
           lightest hover that stays accessible.

           The plain declaration first is a real fallback, not decoration: a
           browser without color-mix drops that line and takes the darker
           signature-strong, which is the design system's own hover
           convention. Without it, the invalid declaration would fall all the
           way back to shepherd's blue. */
        .shepherd-button.shepherd-button-primary:not(:disabled):hover {
          background: var(--signature-strong);
          background: color-mix(in srgb, var(--signature) 92%, var(--surface));
          color: var(--on-signature);
        }

        .shepherd-modal-overlay-container {
          background: var(--dialog-backdrop);
        }

        .shepherd-arrow {
          border-color: var(--surface);
        }

        .shepherd-arrow:before {
          background: var(--surface);
        }
      `}</style>
    </TutorialContext.Provider>
  );
};

export const useTutorialContext = (): TutorialContextValue => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorialContext must be used within a TutorialProvider');
  }
  return context;
};

export default TutorialContext;
